import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";

/**
 * Document search box. On submit (Enter or the search icon) it navigates to the
 * results route `/home/search?q=<query>`; the results page ([pages/Search])
 * derives everything from that `?q=` param, so the URL is the single source of
 * truth. An empty/whitespace-only submit is a no-op.
 *
 * It renders in two places, driven by `variant`:
 * - `hero` — the large, centred Google-style input on the empty search page.
 * - `compact` — the smaller in-page input at the top of the results view, used
 *   to refine the query.
 *
 * The box lives on the search page (above the results, which remount on query
 * change), so a `useEffect` keeps the input reflecting the current `?q=` query
 * whenever the user is on the results route.
 */
const SEARCH_PATH = "/home/search";

interface SearchBarProps {
  /** Visual size/emphasis of the input. Defaults to `compact`. */
  variant?: "hero" | "compact";
  /** Autofocus the input on mount (used for the empty-page hero input). */
  autoFocus?: boolean;
}

const SearchBar: React.FC<SearchBarProps> = ({
  variant = "compact",
  autoFocus = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Reflect the active `?q=` while on the results route. Trim to match how the
  // results page reads the query (`(searchParams.get("q") ?? "").trim()`), so a
  // whitespace-padded URL doesn't make the box disagree with what's searched.
  const urlQuery = (searchParams.get("q") ?? "").trim();
  useEffect(() => {
    if (location.pathname === SEARCH_PATH) {
      setValue(urlQuery);
    }
  }, [location.pathname, urlQuery]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = value.trim();
    if (!q) return; // empty submit is a no-op
    // The typed query is the single source of truth: numeric constraints are
    // parsed out of it server-side (#522/#536), so there is nothing else to
    // carry over — just navigate to the results route with the new `q`.
    navigate(`${SEARCH_PATH}?q=${encodeURIComponent(q)}`);
  };

  const handleClear = () => {
    setValue("");
    inputRef.current?.focus(); // keep the user in the input to retype
  };

  const isHero = variant === "hero";

  // Invite a full natural-language question (Search V4) — the hero input
  // has room for a worked example; the compact refine input stays terse.
  const placeholder = isHero
    ? 'Ask in plain language — e.g. "polymers with Tg between 140 and 160"'
    : "Ask a question or search…";

  // Hide the browser's native search-cancel (×) so it doesn't double up with
  // our custom clear button (WebKit/Chrome render one for type="search").
  const hideNativeClear =
    "[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none";

  const inputClasses = isHero
    ? `w-full pl-14 pr-12 py-4 text-lg text-gray-900 placeholder-gray-400 bg-white border border-gray-300 rounded-full shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#1d365d]/30 focus:border-[#1d365d] transition-shadow ${hideNativeClear}`
    : `w-full pl-11 pr-10 py-2.5 text-sm text-gray-900 placeholder-gray-400 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d]/30 focus:border-[#1d365d] ${hideNativeClear}`;

  return (
    <form
      role="search"
      onSubmit={handleSubmit}
      className={isHero ? "relative w-full max-w-2xl mx-auto" : "relative w-full max-w-2xl"}
    >
      <button
        type="submit"
        aria-label="Search"
        className={`absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#1d365d] transition-colors ${
          isHero ? "left-5" : "left-3.5"
        }`}
      >
        <Search size={isHero ? 20 : 16} />
      </button>
      <input
        ref={inputRef}
        type="search"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        aria-label="Search document contents"
        placeholder={placeholder}
        autoFocus={autoFocus}
        className={inputClasses}
      />
      {value && (
        <button
          type="button"
          onClick={handleClear}
          aria-label="Clear search"
          className={`absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors ${
            isHero ? "right-5" : "right-3"
          }`}
        >
          <X size={isHero ? 20 : 16} />
        </button>
      )}
    </form>
  );
};

export default SearchBar;
